import {
  ActionIcon,
  Button,
  Divider,
  Flex,
  Select,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconChevronRight,
  IconCopyCheck,
  IconFilterStar,
  IconLetterA,
  IconLetterR,
  IconPaperclip,
  IconPlus,
  IconReport,
} from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdvancedFiltersModal, {
  type AdvancedFiltersValues,
  buildSummaryRequestBody,
  buildSummarySearchParams,
} from "src/components/pages/Summary/AdvancedFiltersModal/AdvancedFiltersModal";
import ReviewsTable from "src/components/pages/Summary/ReviewsTable/ReviewsTable";
import { ReviewText } from "src/components/pages/Summary/ReviewsTable/ReviewsTable.styled";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import { useGetApi, useIsOnline, usePostApi, useTheme } from "src/hooks";
import { useSummaryFilters } from "src/hooks/useSummaryFilters";
import { createPdfHead } from "src/mock/createPdfHead";
import { getPdfBody, getPdfSize } from "src/pages/Reports/Report/Report.utils";
import { ButtonsWrapper, PageContainer } from "src/styled";
import { Notification } from "src/utils/Notification";

import MultipleAttachmentModal from "../../components/pages/Summary/Multiple/MultipleAttachmentModal/MultipleAttachmentModal";
import MultipleVerifyModal from "../../components/pages/Summary/Multiple/MultipleVerifyModal/MultipleVerifyModal";
import BackToYachtButton from "../../components/shared/BackToYachtButton/BackToYachtButton";
import useNonMaritime from "../../hooks/useNonMaritime";
import { MEDLBID } from "../../utils";
import ShowNotes from "./ShowNotes/ShowNotes";
import { type IComment } from "./Summary.types";
import { mapSummary } from "./Summary.utils";

const Summary = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isOnline = useIsOnline();
  const { replaceMaritimeText, nonMaritime } = useNonMaritime();
  const { colors } = useTheme();
  const filters = useSummaryFilters();
  const { post, isLoading: pdfLoading } = usePostApi();
  const [pdfExportPending, setPdfExportPending] = useState(false);
  const pdfBody = useRef(null);

  const [
    attachmentModalOpen,
    { open: openAttachmentModal, close: closeAttachmentModal },
  ] = useDisclosure(false);

  const [verifyModalOpen, { close: closeVerifyModal, open: openVerifyModal }] =
    useDisclosure(false);

  const [filtersOpen, { open: openFilters, close: closeFilters }] =
    useDisclosure(false);

  const { data, isLoading, setData, refetch } = useGetApi({
    request: "get-review",
    body: {
      ...buildSummaryRequestBody(
        {
          logbook: filters.logbook,
          status: filters.status.map(String),
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          ref: filters.ref,
          hasSnapshot: filters.hasSnapshot,
          hasAttachments: filters.hasAttachments,
          hasChecklists: filters.hasChecklists,
          sortBy: filters.sort,
          direction: (filters.dir as "asc" | "desc") ?? "asc",
        },
        filters.entriesType,
      ),
      _page: filters.page,
    },
    mapper: mapSummary,
  });
  const setReviewComments = (
    comments: IComment[],
    lbeid: string | undefined,
    partid: string | undefined,
    meid: string | undefined,
    weid: string | undefined,
  ) => {
    if (data) {
      const reviews = data.reviews?.map(r => {
        if (meid) {
          if (r.meid === meid) {
            return { ...r, comments };
          }
        } else if (partid) {
          if (r.partid === partid) {
            return { ...r, comments };
          }
        } else if (weid) {
          if (r.weid === weid) {
            return { ...r, comments };
          }
        } else {
          if (r.lbeid === lbeid) {
            return { ...r, comments };
          }
        }
        return r;
      });

      const required = data.required?.map(r => {
        if (meid) {
          if (r.meid === meid) {
            return { ...r, comments };
          }
        } else if (partid) {
          if (r.partid === partid) {
            return { ...r, comments };
          }
        } else if (weid) {
          if (r.weid === weid) {
            return { ...r, comments };
          }
        } else {
          if (r.lbeid === lbeid) {
            return { ...r, comments };
          }
        }
        return r;
      });

      setData({
        logbooks: data.logbooks,
        pages: data.pages,
        reviews,
        required,
        comments,
        audit: undefined,
        yachtuser: undefined,
        expired: undefined,
        nonauditor: undefined,
        auditor: false,
      });
    }
  };

  const iconSize = 36;
  const verifiedOnly = filters.status[0] === 3 || filters.status[0] === 99;
  const isMedicalLog =
    filters.logbook.length === 1 && (filters.logbook[0] || "*") === MEDLBID;

  const handleCreatePdf = useCallback(async () => {
    if (!data || !pdfBody.current) return;

    try {
      const body = await getPdfBody(pdfBody.current);
      const fileNameParts = ["Summary", filters.entriesType];
      if (filters.logbook.length === 1 && filters.logbook[0] !== "*") {
        fileNameParts.push(filters.logbook[0]);
      }
      fileNameParts.push(`Page_${filters.page}`);

      const fileData = await post({
        request: "create-pdf",
        body: {
          params: {
            head: createPdfHead,
            name: fileNameParts.filter(Boolean).join("_"),
            body,
            scale: 0.75,
            ...getPdfSize("portrait"),
          },
        },
      });

      if (window.api) {
        window.api.downloadFile(
          fileData.file,
          ({ full, path }) => {
            notifications.show({
              title: "Report Downloaded",
              message: (
                <div>
                  <Button
                    variant="subtle"
                    compact
                    onClick={() => {
                      if (window.api) window.api.openFile(full);
                    }}>
                    Open File
                  </Button>
                  <Button
                    variant="subtle"
                    compact
                    onClick={() => {
                      if (window.api) window.api.openFile(path);
                    }}>
                    Open Folder
                  </Button>
                </div>
              ),
            });
          },
          "reports",
        );
      } else {
        window.open(fileData.file, "_blank");
      }
    } catch {
      Notification("Error", "Failed to generate PDF", "red");
    }
  }, [data, filters.entriesType, filters.logbook, filters.page, post, pdfBody]);

  useEffect(() => {
    if (!pdfExportPending) return;

    setPdfExportPending(false);
    void handleCreatePdf();
  }, [handleCreatePdf, pdfExportPending]);

  const handleGeneratePdf = useCallback(
    async (vals: AdvancedFiltersValues) => {
      const params = buildSummarySearchParams(vals, searchParams);
      const requestBody = buildSummaryRequestBody(vals, filters.entriesType);

      navigate(`?${params.toString()}`, { replace: true });
      closeFilters();

      const result = await refetch(requestBody, true);
      if (result) {
        setPdfExportPending(true);
      }
    },
    [closeFilters, filters.entriesType, navigate, refetch, searchParams],
  );

  const onPageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    // dont allow next going to infinity, this uses backend pages set to
    // current page num when the results are empty.
    if (page >= data?.pages) {
      page = data?.pages;
    }
    params.set("page", String(page));
    navigate(`?${params.toString()}`, { replace: true });
  };

  return (
    <PageContainer>
      <Breadcrumbs
        border={false}
        style={{
          position: "sticky",
          top: -15,
          marginTop: -15,
          paddingTop: 12,
          paddingBottom: 12,
          marginBottom: 18,
          background: colors.background?.[0],
          zIndex: 10,
        }}>
        <BackToYachtButton fetchLinks />
        <ButtonsWrapper
          style={{
            flexWrap: "wrap",
            alignItems: "center",
            marginLeft: "12px",
          }}>
          <Flex gap="sm" align="center">
            <ShowNotes />
          </Flex>
          <Flex
            gap="xs"
            align="center"
            style={{ flexGrow: 1, justifyContent: "center" }}>
            <Text c="text">Page(s)</Text>
            <ActionIcon
              size={36}
              variant="default"
              onClick={() => onPageChange(filters.page - 1)}
              disabled={filters.page <= 1}>
              <IconChevronRight
                size={16}
                style={{ transform: "rotate(180deg)" }}
              />
            </ActionIcon>
            <Button size="md" w={36} h={36} p={0} variant="default">
              {filters.page}
            </Button>
            <ActionIcon
              size={36}
              variant="default"
              onClick={() => onPageChange(filters.page + 1)}
              disabled={filters.page >= data?.pages}>
              <IconChevronRight size={16} />
            </ActionIcon>
          </Flex>
          <Tooltip withArrow label={"Advanced Filters"}>
            <ActionIcon
              size={iconSize}
              variant="outline"
              color="blue"
              disabled={!isOnline}
              onClick={openFilters}>
              <IconFilterStar size={22} />
            </ActionIcon>
          </Tooltip>

          {isOnline && (
            <Select
              miw={300}
              searchable
              data={data?.logbooks ?? [{ label: "All Logbooks", value: "*" }]}
              value={
                filters.logbook.length === 1 ? filters.logbook[0] || "*" : null
              }
              placeholder={
                filters.logbook.length > 1
                  ? `Advanced (${filters.logbook.length})`
                  : "No logbooks"
              }
              onChange={v => {
                const params = new URLSearchParams(searchParams);
                if (v) {
                  params.set("logbook", v);
                  params.set("page", "1");
                } else {
                  params.delete("logbook");
                }
                navigate(`?${params.toString()}`, { replace: true });
              }}
            />
          )}

          <Select
            w={150}
            data={[
              { label: "Open", value: "1" },
              { label: replaceMaritimeText("Yacht Verified"), value: "3" },
              { label: "Withdrawn", value: "99" },
            ]}
            value={
              filters.status.length === 1
                ? filters.status[0].toString() || "1"
                : null
            }
            placeholder={
              filters.status.length > 1
                ? `Advanced (${filters.status.length})`
                : "No logbooks"
            }
            onChange={v => {
              const params = new URLSearchParams(searchParams);
              if (v) {
                params.set("status", v);
                params.set("page", "1");
              } else {
                params.delete("status");
              }
              navigate(`?${params.toString()}`, { replace: true });
            }}
          />
          {!nonMaritime && (
            <>
              <Tooltip withArrow label={"Reviews"}>
                <ActionIcon
                  size={iconSize}
                  variant="outline"
                  color="blue"
                  disabled={!isOnline}
                  onClick={() => {
                    navigate("/summary/reviews");
                  }}>
                  <IconLetterR size={22} />
                </ActionIcon>
              </Tooltip>
              <Tooltip withArrow label={"Audits"}>
                <ActionIcon
                  size={iconSize}
                  variant="outline"
                  color="blue"
                  disabled={!isOnline}
                  onClick={() => {
                    navigate("/summary/audits");
                  }}>
                  <IconLetterA size={22} />
                </ActionIcon>
              </Tooltip>
            </>
          )}
          {isOnline && (
            <Tooltip withArrow label={"Multi Verify/Co-Sign"}>
              <ActionIcon
                size={iconSize}
                variant="outline"
                color="blue"
                onClick={() => openVerifyModal()}
                disabled={!isOnline}>
                <IconCopyCheck size={21} />
              </ActionIcon>
            </Tooltip>
          )}
          <Tooltip withArrow label={"Reports"}>
            <ActionIcon
              size={iconSize}
              variant="outline"
              color="blue"
              onClick={() => {
                navigate("/summary/reports");
              }}>
              <IconReport size={22} />
            </ActionIcon>
          </Tooltip>

          {isOnline && (
            <Tooltip withArrow label={"Upload Attachments"}>
              <ActionIcon
                size={iconSize}
                variant="outline"
                color="blue"
                onClick={openAttachmentModal}
                style={{ position: "relative" }}>
                <IconPaperclip
                  size={22}
                  width={34}
                  style={{ marginLeft: -1, marginTop: -1 }}
                />
                <IconPlus
                  size={12}
                  stroke={10}
                  color={colors.background?.[0]}
                  style={{ position: "absolute", bottom: 6, right: 4 }}
                />
                <IconPlus
                  size={12}
                  stroke={4}
                  style={{ position: "absolute", bottom: 6, right: 4 }}
                />
              </ActionIcon>
            </Tooltip>
          )}
        </ButtonsWrapper>
      </Breadcrumbs>
      <Flex direction="column" mr={-12}>
        {!verifiedOnly && (
          <Flex direction="column" mb={12}>
            <ReviewText red ta="center">
              Entries Requiring Verification
            </ReviewText>
            <Divider
              size={1.5}
              color="red"
              orientation="horizontal"
              mr={12}
              mb={-4}
            />
            <ReviewsTable
              red
              isMedicalLog={isMedicalLog}
              reviews={data?.required ?? []}
              setReviewComments={setReviewComments}
              loading={isLoading}
              style={{ height: "100%" }}
            />
          </Flex>
        )}
        <Flex direction="column">
          <ReviewText blue ta="center">
            Entries
          </ReviewText>
          <Divider
            size={1.5}
            color="main.8"
            orientation="horizontal"
            mr={12}
            mb={-4}
          />
          <ReviewsTable
            isMedicalLog={isMedicalLog}
            reviews={
              !verifiedOnly
                ? (data?.reviews ?? [])
                : (data?.reviews.concat(data?.required) ?? []) // FIXME remove concat when backend checks verified filter
            }
            setReviewComments={setReviewComments}
            loading={isLoading}
            style={{ height: "100%" }}
          />
        </Flex>
        <div
          ref={pdfBody}
          style={{
            display: "none",
            position: "absolute",
            left: -9999,
            height: 0,
            width: 0,
          }}>
          <ReviewText blue ta="center">
            Summary Entries
          </ReviewText>
          <ReviewsTable
            isMedicalLog={isMedicalLog}
            reviews={
              !verifiedOnly
                ? (data?.reviews ?? [])
                : (data?.reviews.concat(data?.required) ?? []) // FIXME remove concat when backend checks verified filter
            }
            isPdfExport={true}
            setReviewComments={setReviewComments}
            loading={isLoading}
          />
        </div>
      </Flex>
      <AdvancedFiltersModal
        opened={filtersOpen}
        onClose={closeFilters}
        logbooks={data?.logbooks.filter(lb => lb.value !== "*") ?? []}
        replaceMaritimeText={replaceMaritimeText}
        searchParams={searchParams}
        onGeneratePdf={handleGeneratePdf}
        pdfLoading={pdfLoading || pdfExportPending}
      />
      <MultipleAttachmentModal
        open={attachmentModalOpen}
        close={closeAttachmentModal}
        reviews={data?.reviews ?? []}
        refetch={refetch}
      />
      <MultipleVerifyModal
        open={verifyModalOpen}
        close={closeVerifyModal}
        refetch={refetch}
      />
    </PageContainer>
  );
};

export default Summary;
